// bulkPhotoAssign.js — matching a folder of photos to the defects they belong to.
// ---------------------------------------------------------------------------
// 2026-08-20. Brett walked a site, pinned 25 defects and photographed every one.
// The photos never reached the server (see outbox-refs.js for that bug) and the
// report was, in his words, worthless without images. The originals were still
// on the phone; what was missing was any way to put 25 photos back against 25
// defects without opening each one in turn.
//
// THE ORDER IS THE MATCH. You walk a site in one direction: pin, photograph,
// move on. So the Nth photo taken belongs to the Nth defect raised, and pairing
// them in order gets almost every one right on the first pass. It is a
// starting point, not a claim — every pairing is shown and can be changed
// before anything is uploaded.
//
// WHY NOT MATCH ON TIME. It is the obvious idea and it does not work here. A
// defect's CreatedAt is when the outbox DRAINED, not when it was pinned — the
// 25 above were all stamped within three minutes of each other, hours after the
// inspection, because that is when reception came back. Capture time orders the
// PHOTOS correctly; it says nothing about which defect they belong to.
//
// NOTHING IS UPLOADED THAT HAS NOT BEEN LOOKED AT. Every leftover — a photo
// with no defect, a defect with no photo — is returned and shown, because a
// silent mismatch here re-creates the original failure in a new costume.

/**
 * Capture order. `lastModified` on a File is the camera's timestamp, which is
 * what a phone gallery sorts by. Falling back to the name keeps a stable,
 * predictable order for files that have lost it (some downloads, some
 * cloud round-trips) rather than leaving it to array order.
 */
export function orderPhotos(photos) {
  const list = (Array.isArray(photos) ? photos : []).filter(Boolean);
  return list.slice().sort((a, b) => {
    const at = Number(a.lastModified) || 0;
    const bt = Number(b.lastModified) || 0;
    if (at && bt && at !== bt) return at - bt;
    return String(a.name || '').localeCompare(String(b.name || ''), undefined, { numeric: true, sensitivity: 'base' });
  });
}

/**
 * Pair photos to defects in order.
 *
 * @param {Array} photos   File objects (or anything with name/lastModified)
 * @param {Array} defects  the report's defects, in the order they were raised
 * @returns {{pairs: Array<{photo, defect}>, extraPhotos: Array, unphotographed: Array}}
 */
export function assignInOrder(photos, defects) {
  const ordered = orderPhotos(photos);
  const rows = (Array.isArray(defects) ? defects : []).filter(Boolean);
  const n = Math.min(ordered.length, rows.length);

  const pairs = [];
  for (let i = 0; i < n; i += 1) pairs.push({ photo: ordered[i], defect: rows[i] });

  return {
    pairs,
    // Surfaced, never dropped: more photos than defects usually means two
    // shots of one item, and the person needs to say which.
    extraPhotos: ordered.slice(n),
    // And a defect left without a photo is the thing that makes a report
    // worthless — it has to be visible before the upload, not after.
    unphotographed: rows.slice(n),
  };
}

/**
 * Move one photo onto a different defect, keeping the pairing consistent.
 *
 * A defect can legitimately carry several photos, so this does NOT swap or
 * evict anything — it re-points the one row the person is editing. Passing a
 * null defect detaches it, which is how you skip a photo that does not belong.
 */
export function repoint(pairs, photoIndex, defect) {
  const list = Array.isArray(pairs) ? pairs.slice() : [];
  if (photoIndex < 0 || photoIndex >= list.length) return list;
  list[photoIndex] = { ...list[photoIndex], defect: defect || null };
  return list;
}

/**
 * What is actually going to happen, in a sentence a person can check against
 * the site they remember. Shown before the upload starts.
 */
export function describePlan({ pairs, extraPhotos, unphotographed }) {
  const attaching = (pairs || []).filter(p => p && p.defect).length;
  const bits = [`${attaching} photo${attaching === 1 ? '' : 's'} will be attached`];
  const skipped = (pairs || []).length - attaching + (extraPhotos || []).length;
  if (skipped > 0) bits.push(`${skipped} not assigned to anything`);
  if ((unphotographed || []).length) bits.push(`${unphotographed.length} item${unphotographed.length === 1 ? '' : 's'} will still have no photo`);
  return bits.join(' · ');
}
